import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { friendsApi } from '../api/friends';
import { useAuth } from './AuthContext';

interface FriendsNotificationContextType {
  hasUnreadRequests: boolean;
  unreadCount: number;
  markRequestsAsRead: () => void;
  refreshNotifications: () => Promise<void>;
}

const FriendsNotificationContext = createContext<FriendsNotificationContextType | undefined>(undefined);

export const useFriendsNotification = () => {
  const context = useContext(FriendsNotificationContext);
  if (!context) {
    throw new Error('useFriendsNotification must be used within a FriendsNotificationProvider');
  }
  return context;
};

interface FriendsNotificationProviderProps {
  children: ReactNode;
}

export const FriendsNotificationProvider: React.FC<FriendsNotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [hasUnreadRequests, setHasUnreadRequests] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshNotifications = useCallback(async () => {
    if (!user) {
      setHasUnreadRequests(false);
      setUnreadCount(0);
      return;
    }

    try {
      const receivedRequests = await friendsApi.getReceivedRequests();
      // Read from localStorage directly to avoid dependency on lastChecked state
      const lastCheckedTime = localStorage.getItem(`friendRequests_lastChecked_${user.id}`);

      let newRequestsCount = 0;

      if (lastCheckedTime) {
        // Count requests received after last check
        newRequestsCount = receivedRequests.filter(request =>
          new Date(request.created_at) > new Date(lastCheckedTime)
        ).length;
      } else {
        // If never checked, all pending requests are "new"
        newRequestsCount = receivedRequests.length;
      }

      setUnreadCount(newRequestsCount);
      setHasUnreadRequests(newRequestsCount > 0);
    } catch (error) {
      console.error('Failed to refresh friend request notifications:', error);
      setHasUnreadRequests(false);
      setUnreadCount(0);
    }
  }, [user]);

  const markRequestsAsRead = () => {
    if (!user) return;

    const now = new Date().toISOString();
    localStorage.setItem(`friendRequests_lastChecked_${user.id}`, now);
    setHasUnreadRequests(false);
    setUnreadCount(0);
  };

  useEffect(() => {
    if (user) {
      refreshNotifications();

      // Check for new requests periodically (every 30 seconds)
      const interval = setInterval(refreshNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, refreshNotifications]);

  const value = {
    hasUnreadRequests,
    unreadCount,
    markRequestsAsRead,
    refreshNotifications,
  };

  return (
    <FriendsNotificationContext.Provider value={value}>
      {children}
    </FriendsNotificationContext.Provider>
  );
};